<template>
  <!-- The dashboard Shell has a fixed h-12 topbar, so full min-h-screen here
       would overflow the viewport and scroll behind it. -->
  <div class="bg-gray-50 relative min-h-[calc(100vh-3rem)]">
    <GradientBlobs />

    <div
      class="relative z-10 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-8 min-h-[calc(100vh-3rem)]"
    >
      <!-- Processing State -->
      <SubscriptionProcessingState v-if="isProcessing" />

      <!-- Success State -->
      <SuccessState v-else-if="isSuccess" />

      <!-- Error State -->
      <SubscriptionErrorState
        v-else-if="currentError"
        :message="currentError"
        @retry="handleRetryPayment"
      />

      <!-- Loading State -->
      <SubscriptionLoadingState v-else-if="isLoading" />

      <!-- Not Found State -->
      <SubscriptionPlansNotFoundState
        v-else-if="isNotFound"
      />

      <!-- Product Package Offer -->
      <ProductDisplay
        v-else-if="showProductOffer"
        :product="plansStore.assignedProduct"
        :purchase="plansStore.productAlreadyPurchased ? plansStore.unexpiredPurchase : null"
        :is-loading="isProductCheckoutLoading"
        :has-active-subscription="hasActiveSubscription"
        @pay="handleProductPay"
      />

      <!-- Plan Display -->
      <PlanDisplay
        v-else-if="showContent"
        :plan="currentPlan"
        :is-loading="isCheckoutLoading"
        @pay="handlePay"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { usePlansStore, useSubscriptionsStore, initializeSubscriptionData } from '@/stores/subscription'
import { useCheckout } from '@/composables/useCheckout'
import { APP_ROUTES } from '@/const/appRoutes'

// Components
import GradientBlobs from '@/components/ui/GradientBlobs.vue'
import SubscriptionLoadingState from '@/components/subscription/SubscriptionLoadingState.vue'
import SubscriptionProcessingState from '@/components/subscription/SubscriptionProcessingState.vue'
import SubscriptionErrorState from '@/components/subscription/SubscriptionErrorState.vue'
import SubscriptionPlansNotFoundState from '@/components/subscription/SubscriptionPlansNotFoundState.vue'
import SuccessState from '@/components/subscription/SuccessState.vue'
import PlanDisplay from '@/components/subscription/PlanDisplay.vue'
import ProductDisplay from '@/components/subscription/ProductDisplay.vue'

const router = useRouter()
const route = useRoute()
const plansStore = usePlansStore()
const subscriptionsStore = useSubscriptionsStore()

const isProductCheckoutLoading = ref(false)

const auth = useCheckout({
  successRedirectRoute: APP_ROUTES.TUITION_MANAGE,
  successRedirectDelay: 2000
})

const hasActiveSubscription = computed(() => {
  const subscription = subscriptionsStore.subscriptions[0]
  return Boolean(subscription && ['active', 'trialing'].includes(subscription.status))
})

const showProductOffer = computed(() => {
  // Purchasable offer, or an already-purchased one shown in its "purchased" state.
  return plansStore.canPurchaseProduct || plansStore.productAlreadyPurchased
})

const isProcessing = computed(() => auth.isProcessingCheckout.value)

const isSuccess = computed(() => auth.checkoutSuccess.value)

const currentError = computed(() => auth.checkoutError.value)

const isLoading = computed(
  () => plansStore.loading && !auth.isProcessingCheckout.value && !auth.checkoutSuccess.value
)

const isNotFound = computed(() => {
  if (showProductOffer.value) return false
  return plansStore.error?.includes('404') || plansStore.error?.includes('Not Found')
})

const currentPlan = computed(() => plansStore.plans[0] || null)

// An assigned plan that differs from the current subscription's plan is a
// next-plan offer (e.g. Genesis -> Atlas) and stays purchasable mid-subscription.
const hasNextPlanOffer = computed(() => {
  const subscription = subscriptionsStore.subscriptions[0]
  const offered = plansStore.plans[0]
  if (!subscription || !offered) return false
  const currentPlanId = typeof subscription.plan === 'number' ? subscription.plan : subscription.plan?.id
  return currentPlanId !== offered.id
})

const showContent = computed(() => {
  if (showProductOffer.value) return false
  const subscription = subscriptionsStore.subscriptions[0]
  const canView =
    !subscription ||
    ['canceled', 'incomplete_expired'].includes(subscription.status) ||
    hasNextPlanOffer.value
  return currentPlan.value && !auth.isProcessingCheckout.value && !auth.checkoutSuccess.value && canView
})

const isCheckoutLoading = computed(() => auth.isCreatingCheckout.value)

// Methods
const handleRetryPayment = async () => {
  auth.clearError()
  if (showProductOffer.value) {
    await handleProductPay()
    return
  }
  await handlePay()
}

const handlePay = async () => {
  if (!currentPlan.value) return
  const planIdToUse = currentPlan.value.public_id || currentPlan.value.id
  await auth.createCheckout(planIdToUse)
}

const handleProductPay = async () => {
  const product = plansStore.assignedProduct
  if (!product?.public_id) return

  isProductCheckoutLoading.value = true
  try {
    const checkoutData = await plansStore.createProductPurchaseCheckout(product.public_id)
    if (!checkoutData.session_url) {
      throw new Error('No session URL received from server')
    }
    window.location.href = checkoutData.session_url
  } catch (error) {
    const e = error as { response?: { data?: { error?: string } }; message?: string }
    auth.checkoutError.value =
      e.response?.data?.error ||
      e.message ||
      'Failed to create checkout session'
  } finally {
    isProductCheckoutLoading.value = false
  }
}

// Lifecycle
onMounted(async () => {
  await initializeSubscriptionData()

  const hasCheckoutQuery = Boolean(route.query.session_id) || route.query.cancelled === 'true'
  const subscription = subscriptionsStore.subscriptions[0]
  const shouldRedirect =
    subscription &&
    !['canceled', 'incomplete_expired'].includes(subscription.status) &&
    !plansStore.canPurchaseProduct &&
    // Purchasers stay here and see the offer card in its "purchased" state.
    !plansStore.productAlreadyPurchased &&
    !hasNextPlanOffer.value

  if (!hasCheckoutQuery && shouldRedirect) {
    await router.replace(APP_ROUTES.TUITION_MANAGE)
    return
  }

  await auth.handleCheckoutReturn(route)

  if (shouldRedirect) {
    await router.replace(APP_ROUTES.TUITION_MANAGE)
  }
})
</script>
